import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingScreen from './LoadingScreen';
import StartScreen from './StartScreen';
import TutorialOverlay from './TutorialOverlay';
import FloatingMenu from './FloatingMenu';
import MenuPopup from './MenuPopup';
import GalleryHeader from './GalleryHeader';
import ExpositionsView from './ExpositionsView';
import EventsView from './EventsView';
import EventDetailsView from './EventDetailsView';
import CatalogView from './CatalogView';
import CallbackForm from './CallbackForm';
import ConfirmationDialog from './ConfirmationDialog';
import ItemDetailsView from './ItemDetailsView';
import JoystickUI from './JoystickUI';
import { useGalleryData } from '../hooks/useGalleryData';
import { useGalleryEngine } from '../hooks/useGalleryEngine';
import { useUIState } from '../hooks/useUIState';
import { useGalleryInteractions } from '../hooks/useGalleryInteractions';
import { useLoadingManager } from '../hooks/useLoadingManager';

function VirtualGallery() {
  const { t } = useTranslation();
  const { hallId } = useParams();
  
  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(false);
  
  // Track when loading started for custom rooms to prevent premature error display
  const [customRoomLoadStartTime, setCustomRoomLoadStartTime] = useState(null);
  
  // Check if user has seen tutorial before
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('gallery-tutorial-completed');
    if (!hasSeenTutorial) {
      // Small delay to ensure the scene is ready before showing tutorial
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  // Loading manager to track ALL async operations
  const loadingManager = useLoadingManager();
  const [floatingMenuNavigation, setFloatingMenuNavigation] = useState(null);

  // Clear floating menu navigation after it's processed
  useEffect(() => {
    if (floatingMenuNavigation) {
      const timer = setTimeout(() => {
        setFloatingMenuNavigation(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [floatingMenuNavigation]);
  
  // Custom hooks for data and state management
  const {
    showrooms,
    items,
    placements,
    currentShowroomId,
    setCurrentShowroomId,
    currentShowroom,
    loading,
    sceneLoading,
    setSceneLoading
  } = useGalleryData(hallId);
  
  const {
    canvasRef,
    engineRef,
    engineReady,
    sceneReady,
    setSceneReady,
    updateScene,
    setOnInteraction,
    backgroundLoadingProgress,
    roomReadiness
  } = useGalleryEngine(loadingManager);
  
  const {
    showStartScreen,
    setShowStartScreen,
    skipStartScreen,
    setSkipStartScreen,
    menuPopup,
    itemDetailsPopup,
    confirmation,
    handleMenuItemClick,
    handleCloseMenu,
    handleMenuNavigation,
    handleMenuBack,
    handleShowItemDetails,
    handleCloseItemDetails,
    handleShowConfirmation,
    handleCloseConfirmation
  } = useUIState();

  // Interaction handling
  const {
    handleEngineInteraction,
    handleTeleportToRoom,
    handleTeleportToItem,
    processPendingTeleportation
  } = useGalleryInteractions({
    currentShowroomId,
    setCurrentShowroomId,
    showrooms,
    setSkipStartScreen,
    setShowStartScreen,
    handleCloseMenu,
    handleCloseConfirmation,
    handleShowItemDetails,
    handleCloseItemDetails,
    setFloatingMenuNavigation,
    engineRef,
    setSceneReady,
    loadingManager
  });

  // Set interaction callback when engine is ready
  useEffect(() => {
    setOnInteraction(handleEngineInteraction);
  }, [setOnInteraction, handleEngineInteraction]);

  // Track custom room loading start time
  useEffect(() => {
    if (currentShowroom?.modelType === 'custom' && engineReady && !loading) {
      setCustomRoomLoadStartTime(Date.now());
    }
  }, [currentShowroom, engineReady, loading]);

  // Reset load start time when scene becomes ready
  useEffect(() => {
    if (sceneReady) {
      setCustomRoomLoadStartTime(null);
    }
  }, [sceneReady]);

  // Load Cleversite plugin after scene is ready
  useEffect(() => {
    if (sceneReady && !document.querySelector('script[src*="cleversite"]')) {
      const script = document.createElement('script');
      script.src = '//widget.cleversite.ru/widget/132812/206324/';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [sceneReady]);

  // Update scene when data changes
  useEffect(() => {
    if (engineReady && currentShowroom && !loading) {
      updateScene(currentShowroom, placements, items, currentShowroomId, setSceneLoading, showrooms);
    }
  }, [engineReady, currentShowroom, loading, updateScene, items, placements, currentShowroomId, setSceneLoading, showrooms]);

  // Process pending cross-room teleportations when scene becomes ready
  useEffect(() => {
    if (sceneReady && engineReady) {
      processPendingTeleportation();
    }
  }, [sceneReady, engineReady, processPendingTeleportation]);

  // Check if there's a custom room loading error - only show after reasonable time has passed
  const hasCustomRoomError = currentShowroom?.modelType === 'custom' &&
                              !sceneReady &&
                              !loading &&
                              !sceneLoading &&
                              !loadingManager.isLoading &&
                              customRoomLoadStartTime &&
                              (Date.now() - customRoomLoadStartTime > 10000); // Wait at least 10 seconds before showing error

  // Header handlers
  const handleOpenTerminal = () => {
    setFloatingMenuNavigation({ action: 'open', view: 'menu' });
  };

  const handleShowTutorialFromHeader = () => {
    setShowTutorial(true);
  };

  return (
    <>
      {/* Always render canvas first - NEVER conditional */}
      <div 
        ref={canvasRef} 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          visibility: (loading || !sceneReady || showStartScreen) ? 'hidden' : 'visible'
        }} 
      />

      {/* Start Screen */}
      {showStartScreen && !skipStartScreen && (
        <StartScreen onStart={() => setShowStartScreen(false)} />
      )}

      {/* Tutorial Overlay - shown after scene is ready and user hasn't seen it */}
      {showTutorial && sceneReady && !showStartScreen && (
        <TutorialOverlay onClose={() => setShowTutorial(false)} />
      )}

      {/* Show loading screen overlay - now includes ALL async operations */}
      {(loading || sceneLoading || !sceneReady || loadingManager.isLoading) && !showStartScreen && <LoadingScreen />}

      {/* Custom room loading error */}
      {hasCustomRoomError && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          color: 'white', 
          fontSize: '18px',
          backgroundColor: 'rgba(0,0,0,0.9)',
          padding: '30px',
          borderRadius: '12px',
          zIndex: 100,
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginBottom: '16px', color: '#ff6b6b' }}>Custom Room Loading Failed</h2>
          <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
            The 3D model for "{currentShowroom.name}" could not be loaded.
          </p>
          <p style={{ marginBottom: '20px', fontSize: '14px', color: '#ccc' }}>
            This might be due to:
            <br />• Large file size exceeding device memory
            <br />• Network connection issues
            <br />• Corrupted 3D model file
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* No showroom error */}
      {!currentShowroom && !loading && !showStartScreen && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          color: 'white', 
          fontSize: '22px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: '20px',
          borderRadius: '8px',
          zIndex: 100
        }}>
          <h2>No Showroom Found!</h2>
          <p>Available showrooms: {showrooms.length}</p>
          <p>Looking for ID: {currentShowroomId}</p>
          <p>Create a showroom in the admin panel first.</p>
        </div>
      )}

      {/* Gallery Header - show when scene is ready and not on start screen */}
      {sceneReady && !showStartScreen && (
        <GalleryHeader
          onOpenTerminal={handleOpenTerminal}
          onShowTutorial={handleShowTutorialFromHeader}
          showroomName={currentShowroom?.name}
        />
      )}

      {/* React UI Components - only show when ready */}
      {sceneReady && (
        <FloatingMenu
          onMenuItemClick={handleMenuItemClick}
          onTeleportToItem={handleTeleportToItem}
          onTeleportToRoom={handleTeleportToRoom}
          onShowConfirmation={handleShowConfirmation}
          currentShowroomId={currentShowroomId}
          backgroundLoadingProgress={backgroundLoadingProgress}
          roomReadiness={roomReadiness}
          externalNavigation={floatingMenuNavigation}
        />
      )}

      {/* Menu Popup */}
      {menuPopup.isOpen && (
        <MenuPopup
          isOpen={menuPopup.isOpen}
          onClose={handleCloseMenu}
          title={
            menuPopup.type === 'expositions' ? t('expositions') :
            menuPopup.type === 'events' ? t('events') :
            menuPopup.type === 'catalog' ? t('catalog') :
            menuPopup.type === 'callback' ? t('contact') :
            menuPopup.type === 'event-details' ? t('eventDetails') :
            t('menu')
          }
          onBack={handleMenuBack}
          showBackButton={menuPopup.navigationStack.length > 0 || menuPopup.type === 'event-details'}
        >
          {menuPopup.type === 'expositions' && (
            <ExpositionsView
              onTeleportToRoom={handleTeleportToRoom}
              currentShowroomId={currentShowroomId}
              onShowConfirmation={handleShowConfirmation}
            />
          )}
          
          {menuPopup.type === 'events' && (
            <EventsView
              onShowEventDetails={(event) => handleMenuNavigation('event-details', event)}
              onBack={handleMenuBack}
            />
          )}
          
          {menuPopup.type === 'event-details' && menuPopup.data && (
            <EventDetailsView
              event={menuPopup.data}
              onBack={handleMenuBack}
              onRegister={(event) => {
                // Handle event registration
              }}
            />
          )}
          
          {menuPopup.type === 'catalog' && (
            <CatalogView
              onTeleportToItem={handleTeleportToItem}
              onShowItemDetails={(item, showrooms) => handleMenuNavigation('item-details', { item, showrooms })}
              currentShowroomId={currentShowroomId}
              onShowConfirmation={handleShowConfirmation}
              backgroundLoadingProgress={backgroundLoadingProgress}
              roomReadiness={roomReadiness}
            />
          )}

          {menuPopup.type === 'item-details' && menuPopup.data && (
            <ItemDetailsView
              data={menuPopup.data}
              onTeleportToItem={handleTeleportToItem}
              onShowConfirmation={handleShowConfirmation}
              currentShowroomId={currentShowroomId}
              onBack={handleMenuBack}
            />
          )}

          {menuPopup.type === 'callback' && (
            <CallbackForm onClose={handleCloseMenu} />
          )}
        </MenuPopup>
      )}

      {/* Item Details Popup */}
      {itemDetailsPopup.isOpen && itemDetailsPopup.data && (
        <MenuPopup
          isOpen={itemDetailsPopup.isOpen}
          onClose={handleCloseItemDetails}
          title={itemDetailsPopup.data?.item?.name || t('itemDetails')}
          showBackButton={false}
        >
          <ItemDetailsView
            data={itemDetailsPopup.data}
            onTeleportToItem={handleTeleportToItem}
            onShowConfirmation={handleShowConfirmation}
            currentShowroomId={currentShowroomId}
          />
        </MenuPopup>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={() => {
          if (confirmation.onConfirm) {
            confirmation.onConfirm();
          }
          handleCloseConfirmation();
        }}
        onCancel={handleCloseConfirmation}
      />

      {/* Mobile Joystick UI - only show when scene is ready, disable when modals are open */}
      {sceneReady && engineRef?.current?.cameraController && (
        <JoystickUI
          cameraController={engineRef.current.cameraController}
          disabled={menuPopup.isOpen || itemDetailsPopup.isOpen || confirmation.isOpen || showTutorial}
        />
      )}
    </>
  );
}

export default VirtualGallery;